import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, RotateCcw, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

export const AD_SRC = '/media/accu-desk-ad.mp4';
export const AD_POSTER = '/media/ad-poster.jpg';

export const VideoThumbnail = ({ onPlay }) => (
  <button type="button" onClick={onPlay} className="group relative block w-full text-left rounded-2xl overflow-hidden shadow-2xl ring-1 ring-stone-900/10 bg-stone-950 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-300" data-testid="hero-video-thumbnail">
    <img src={AD_POSTER} alt="Accu Desk 40 second product tour" className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.03]"/>
    <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-stone-950/10 to-transparent"/>
    <span className="absolute inset-0 flex items-center justify-center">
      <span className="absolute h-24 w-24 rounded-full bg-sky-400/40 animate-ping [animation-duration:2.4s]"/>
      <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-white text-emerald-800 shadow-[0_0_50px_rgba(56,189,248,0.6)] transition-transform duration-300 group-hover:scale-110" data-testid="hero-video-play-btn">
        <Play size={30} className="ml-1 fill-current"/>
      </span>
    </span>
    <span className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-stone-900 shadow-lg" data-testid="hero-video-label">
      <span className="h-2 w-2 rounded-full bg-red-500"/> Watch the 40s tour
    </span>
  </button>
);

export const VideoLightbox = ({ open, onOpenChange }) => {
  const ref = useRef(null);
  const [ended, setEnded] = useState(false);
  const replay = () => { setEnded(false); ref.current.currentTime = 0; ref.current.play(); };
  const change = (o) => { if (!o) setEnded(false); onOpenChange(o); };
  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="max-w-[min(92vw,1280px)] w-full p-0 border-0 bg-black overflow-hidden sm:rounded-2xl [&>button]:hidden" data-testid="hero-video-lightbox">
        <DialogTitle className="sr-only">Accu Desk product tour</DialogTitle>
        <div className="relative aspect-video">
          <video ref={ref} src={AD_SRC} poster={AD_POSTER} autoPlay controls playsInline onEnded={() => setEnded(true)} onPlay={() => setEnded(false)} className="h-full w-full" data-testid="hero-video-player"/>
          {ended && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-stone-950/85 backdrop-blur-sm text-center px-6 animate-in fade-in duration-500" data-testid="hero-video-end-cta">
              <h3 className="font-serif text-3xl sm:text-4xl text-white">Run your whole practice in one place.</h3>
              <p className="text-stone-300">30-day free trial · No credit card required</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/signup"><Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white h-12 px-6" data-testid="hero-video-trial-btn">Start your free trial <ArrowRight size={16} className="ml-2"/></Button></Link>
                <Button size="lg" variant="outline" onClick={replay} className="h-12 px-6 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="hero-video-replay-btn"><RotateCcw size={16} className="mr-2"/> Watch again</Button>
              </div>
            </div>
          )}
          <button onClick={() => change(false)} className="absolute top-3 right-3 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black/80" aria-label="Close video" data-testid="hero-video-close-btn"><X size={18}/></button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
